import { useEffect } from 'react';
import { track } from '../analytics/track';

/**
 * Hook to track ElevenLabs conversational AI widget interactions
 * Tracks two distinct events:
 * 1. Eva Widget Opened - when the widget is opened/clicked
 * 2. Eva Conversation Started - when the user actually starts talking/typing
 */
export const useElevenLabsTracking = () => {
  useEffect(() => {
    let widgetOpened = false;
    let conversationStarted = false;

    const trackWidgetOpen = () => {
      // Prevent duplicate tracking
      if (widgetOpened) return;
      
      widgetOpened = true;
      
      // Track the widget open event
      track({
        event: 'Eva Widget Opened',
        data: {
          timestamp: new Date().toISOString(),
          source: 'elevenlabs_widget',
          page_url: window.location.href,
          page_path: window.location.pathname,
        },
      });
      
      console.log('Eva Widget Opened event tracked');
    };

    const trackConversationStart = () => {
      // Prevent duplicate tracking
      if (conversationStarted) return;
      
      conversationStarted = true;
      
      // Track the conversation start event
      track({
        event: 'Eva Conversation Started',
        data: {
          timestamp: new Date().toISOString(),
          source: 'elevenlabs_widget',
          page_url: window.location.href,
          page_path: window.location.pathname,
          widget_was_opened: widgetOpened, // Track if widget was already opened
        },
      });
      
      console.log('Eva Conversation Started event tracked');
    };

    // Handle widget open events
    const handleWidgetOpen = (event: CustomEvent) => {
      trackWidgetOpen();
    };

    // Handle conversation start events
    const handleConversationStart = (event: CustomEvent) => {
      // Ensure widget open is tracked first
      trackWidgetOpen();
      trackConversationStart();
    };

    // Handle user message events (indicates actual conversation)
    const handleUserMessage = (event: CustomEvent) => {
      trackConversationStart();
    };

    // Handle AI response events (indicates conversation is active)
    const handleAIResponse = (event: CustomEvent) => {
      trackConversationStart();
    };

    // Method 1: Monitor for widget interaction through DOM changes
    const observeWidget = () => {
      const widgetElement = document.querySelector('elevenlabs-convai');
      
      if (!widgetElement) {
        // Widget not found yet, retry
        setTimeout(observeWidget, 1000);
        return;
      }

      let previousState: string | null = null;
      let isOpen = false;

      // Create a MutationObserver to watch for widget state changes
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes') {
            const target = mutation.target as HTMLElement;
            const currentState = target.getAttribute('data-state');
            const isActive = target.getAttribute('data-active');
            
            // Detect widget opening
            if (
              (currentState === 'open' && previousState !== 'open') ||
              (isActive === 'true' && !isOpen) ||
              target.classList.contains('open') ||
              target.classList.contains('active')
            ) {
              isOpen = true;
              trackWidgetOpen();
            }
            
            // Detect conversation starting (look for additional states)
            if (
              currentState === 'conversation' ||
              currentState === 'speaking' ||
              currentState === 'listening' ||
              target.getAttribute('data-conversation-active') === 'true'
            ) {
              trackConversationStart();
            }
            
            previousState = currentState;
          }
        });
      });

      // Start observing the widget element
      observer.observe(widgetElement, {
        attributes: true,
        attributeFilter: ['class', 'data-state', 'data-active', 'data-conversation-active'],
        subtree: true, // Also observe child elements
      });

      // Cleanup function
      return () => {
        observer.disconnect();
      };
    };

    // Method 2: Listen for clicks on the widget launcher button
    const handleWidgetClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Check if the clicked element is part of the ElevenLabs widget launcher
      if (
        target.closest('elevenlabs-convai') ||
        target.closest('[data-elevenlabs-widget]') ||
        target.id === 'elevenlabs-widget-launcher' ||
        target.classList.contains('elevenlabs-launcher')
      ) {
        // This is just opening the widget, not starting conversation yet
        trackWidgetOpen();
      }
    };

    // Set up event listeners for widget open
    window.addEventListener('elevenlabs:widget:open', handleWidgetOpen as EventListener);
    window.addEventListener('elevenlabs:widget:opened', handleWidgetOpen as EventListener);
    window.addEventListener('widget:open', handleWidgetOpen as EventListener);
    
    // Set up event listeners for conversation start
    window.addEventListener('elevenlabs:conversation:start', handleConversationStart as EventListener);
    window.addEventListener('elevenlabs:conversation:started', handleConversationStart as EventListener);
    window.addEventListener('elevenlabs:user:message', handleUserMessage as EventListener);
    window.addEventListener('elevenlabs:agent:message', handleAIResponse as EventListener);
    window.addEventListener('elevenlabs:audio:start', handleConversationStart as EventListener);
    window.addEventListener('conversation:start', handleConversationStart as EventListener);
    
    // Listen for clicks
    document.addEventListener('click', handleWidgetClick, true);

    // Start observing for widget
    const cleanupObserver = observeWidget();

    // Additional method: Check for widget iframe or shadow DOM interactions
    const checkForWidgetInteraction = () => {
      const widget = document.querySelector('elevenlabs-convai');
      
      if (widget) {
        // Check if widget has shadow DOM
        const shadowRoot = (widget as any).shadowRoot;
        
        if (shadowRoot) {
          // Listen for clicks on launcher button (opens widget)
          const launcher = shadowRoot.querySelector('[role="button"]') || 
                          shadowRoot.querySelector('.launcher') ||
                          shadowRoot.querySelector('.widget-launcher');
          
          if (launcher) {
            launcher.addEventListener('click', () => trackWidgetOpen());
          }
          
          // Listen for interaction elements that indicate conversation start
          const inputField = shadowRoot.querySelector('input, textarea, [contenteditable="true"]');
          const micButton = shadowRoot.querySelector('[aria-label*="mic"], [aria-label*="microphone"], .mic-button');
          
          if (inputField) {
            inputField.addEventListener('focus', () => trackConversationStart());
            inputField.addEventListener('input', () => trackConversationStart());
          }
          
          if (micButton) {
            micButton.addEventListener('click', () => trackConversationStart());
          }
        }
      }
    };

    // Delay to ensure widget is loaded
    setTimeout(checkForWidgetInteraction, 2000);

    // Method to detect WebSocket connections (indicates conversation)
    const detectWebSocketConnection = () => {
      const originalWebSocket = window.WebSocket;
      window.WebSocket = function(url: string | URL, protocols?: string | string[]) {
        const ws = new originalWebSocket(url, protocols);
        
        // Check if this is an ElevenLabs WebSocket
        const urlString = typeof url === 'string' ? url : url.toString();
        if (urlString && (urlString.includes('elevenlabs') || urlString.includes('eleven'))) {
          // Track conversation start when WebSocket opens
          ws.addEventListener('open', () => {
            trackConversationStart();
          });
        }
        
        return ws;
      } as any;
    };
    
    // Start WebSocket detection
    detectWebSocketConnection();

    // Cleanup
    return () => {
      // Remove widget open listeners
      window.removeEventListener('elevenlabs:widget:open', handleWidgetOpen as EventListener);
      window.removeEventListener('elevenlabs:widget:opened', handleWidgetOpen as EventListener);
      window.removeEventListener('widget:open', handleWidgetOpen as EventListener);
      
      // Remove conversation start listeners
      window.removeEventListener('elevenlabs:conversation:start', handleConversationStart as EventListener);
      window.removeEventListener('elevenlabs:conversation:started', handleConversationStart as EventListener);
      window.removeEventListener('elevenlabs:user:message', handleUserMessage as EventListener);
      window.removeEventListener('elevenlabs:agent:message', handleAIResponse as EventListener);
      window.removeEventListener('elevenlabs:audio:start', handleConversationStart as EventListener);
      window.removeEventListener('conversation:start', handleConversationStart as EventListener);
      
      // Remove click listener
      document.removeEventListener('click', handleWidgetClick, true);
      
      // Reset tracking flags
      widgetOpened = false;
      conversationStarted = false;
      
      if (cleanupObserver) {
        cleanupObserver();
      }
    };
  }, []);
};


